import {
  buildFormPayloadContract,
  generateDebugPayloadFromFormFields,
} from '../../utils/formDesignToStudioV2Fields';
import { SF_ADVISOR_FORM_ELEMENTS } from '../../data/sfAdvisorIntake';

const clone = (x) => JSON.parse(JSON.stringify(x));

const candidateScreeningFormElements = [
  {
    id: 'candidateName',
    type: 'text',
    label: 'Candidate name',
    required: true,
    placeholder: 'Jordan Lee',
    description: 'Full name as shown on the resume.',
  },
  {
    id: 'roleApplied',
    type: 'text',
    label: 'Role applied',
    required: true,
    placeholder: 'Senior Backend Engineer',
  },
  {
    id: 'yearsExperience',
    type: 'number',
    label: 'Years of experience',
    required: true,
    minimum: 0,
    maximum: 60,
    defaultValue: 0,
  },
  {
    id: 'location',
    type: 'text',
    label: 'Location / timezone',
    required: false,
    placeholder: 'Remote — US Eastern',
  },
  {
    id: 'salaryBand',
    type: 'text',
    label: 'Salary expectation (band)',
    required: false,
    placeholder: '$180k–$200k',
  },
  {
    id: 'roleLevel',
    type: 'listbox',
    label: 'Level',
    required: true,
    options: ['junior', 'mid', 'senior', 'staff'],
    defaultValue: 'senior',
  },
  {
    id: 'visaRequiresSponsorship',
    type: 'checkbox',
    label: 'Candidate requires visa sponsorship',
    required: false,
    defaultValue: false,
  },
  {
    id: 'resumeNotes',
    type: 'textarea',
    label: 'Resume highlights / notes',
    required: false,
    placeholder: 'Paste bullets or recruiter notes for the rubric.',
  },
  {
    id: 'cvAttachmentRef',
    type: 'text',
    label: 'Résumé / CV reference',
    required: false,
    placeholder: 'External storage URL or ATS file id (metadata only)',
  },
];

const applicationTypeAdvisorFormElements = SF_ADVISOR_FORM_ELEMENTS;

const interviewFeedbackFormElements = [
  {
    id: 'candidateName',
    type: 'text',
    label: 'Candidate name',
    required: true,
    placeholder: 'Jordan Lee',
  },
  {
    id: 'roleApplied',
    type: 'text',
    label: 'Role',
    required: true,
    placeholder: 'Senior Backend Engineer',
  },
  {
    id: 'interviewerName',
    type: 'text',
    label: 'Interviewer',
    required: false,
    placeholder: 'Alex Morgan',
  },
  {
    id: 'screeningBucket',
    type: 'listbox',
    label: 'Screening bucket (from screening console)',
    required: false,
    options: ['pass', 'hold', 'reject'],
  },
  {
    id: 'screeningScore',
    type: 'number',
    label: 'Screening score (handoff)',
    required: false,
    minimum: 0,
    maximum: 100,
  },
  {
    id: 'technicalDepth',
    type: 'number',
    label: 'Technical depth (1–5)',
    required: true,
    minimum: 1,
    maximum: 5,
    defaultValue: 3,
  },
  {
    id: 'communication',
    type: 'number',
    label: 'Communication (1–5)',
    required: true,
    minimum: 1,
    maximum: 5,
    defaultValue: 3,
  },
  {
    id: 'problemSolving',
    type: 'number',
    label: 'Problem solving (1–5)',
    required: true,
    minimum: 1,
    maximum: 5,
    defaultValue: 3,
  },
  {
    id: 'cultureFit',
    type: 'number',
    label: 'Culture fit (1–5)',
    required: false,
    minimum: 1,
    maximum: 5,
    defaultValue: 3,
  },
  {
    id: 'roleAlignment',
    type: 'number',
    label: 'Role alignment (1–5)',
    required: false,
    minimum: 1,
    maximum: 5,
    defaultValue: 3,
  },
  {
    id: 'recommendation',
    type: 'listbox',
    label: 'Hiring recommendation',
    required: true,
    options: ['hire', 'hold', 'no'],
    defaultValue: 'hold',
  },
  {
    id: 'interviewNotes',
    type: 'textarea',
    label: 'Interview notes / debrief',
    required: true,
    placeholder: 'Structured notes, Q&A highlights, and concerns.',
  },
];

function buildTemplate(definition) {
  const payloadContract = buildFormPayloadContract(definition.formElements, {
    id: definition.id,
    name: definition.name,
  });
  return {
    ...definition,
    payloadContract,
    debugPayload: generateDebugPayloadFromFormFields(payloadContract.fields),
  };
}

export const STUDIO_V2_FORM_TEMPLATES = [
  {
    id: 'candidate-screening-intake',
    label: 'Candidate screening intake',
    description: 'Recruiter-friendly intake form that maps cleanly to the candidate screening flow input.',
    category: 'recruiting',
    tags: ['recruiting', 'screening', 'form', 'handoff'],
    definition: buildTemplate({
      id: 'form_tpl_candidate_screening',
      name: 'Candidate screening intake',
      description: 'Collect candidate facts before running the structured screening flow.',
      formElements: clone(candidateScreeningFormElements),
      formSettings: {
        title: 'Candidate screening intake',
        description: 'Capture candidate context for automated structured screening.',
        submitLabel: 'Start screening',
      },
    }),
  },
  {
    id: 'interview-feedback',
    label: 'Interview feedback & scorecard',
    description: 'Post-interview debrief form for the scorecard flow (Playbook #3). Supports screening handoff fields.',
    category: 'recruiting',
    tags: ['recruiting', 'scorecard', 'form', 'handoff'],
    definition: buildTemplate({
      id: 'form_tpl_interview_feedback',
      name: 'Interview feedback intake',
      description: 'Capture dimension scores and notes before generating manager summary and scorecard.',
      formElements: clone(interviewFeedbackFormElements),
      formSettings: {
        title: 'Interview debrief',
        description: 'Submit feedback to generate a structured hiring scorecard and summary.',
        submitLabel: 'Generate scorecard',
      },
    }),
  },
  {
    id: 'application-type-intake',
    label: 'SF application-type intake',
    description: 'Citizen project intent for the San Francisco permit / planning / license advisor (CLAW tool).',
    category: 'government',
    tags: ['permits', 'planning', 'license', 'san-francisco', 'claw', 'form'],
    definition: buildTemplate({
      id: 'form_tpl_application_type_advisor',
      name: 'SF application-type intake',
      description: 'Collect project intent before running if_advise_application_type.',
      formElements: clone(applicationTypeAdvisorFormElements),
      formSettings: {
        title: 'What are you trying to do in San Francisco?',
        description: 'We recommend whether to start with a building permit, planning review, or business registration.',
        submitLabel: 'Get recommendation',
      },
    }),
  },
];

export const STUDIO_V2_USE_CASE_BUNDLES = [
  {
    id: 'candidate-screening',
    label: 'Structured candidate screening',
    description: 'Creates the intake form, screening flow, and console app starter for use case #1.',
    formTemplateId: 'candidate-screening-intake',
    flowTemplateId: 'candidate-screening',
    appTemplateId: 'candidate-screening-console',
    tags: ['recruiting', 'screening', 'bundle'],
  },
  {
    id: 'interview-scorecard',
    label: 'Post-interview summary & scorecard',
    description: 'Creates feedback form, scorecard flow, and manager console for use case #3.',
    formTemplateId: 'interview-feedback',
    flowTemplateId: 'interview-scorecard',
    appTemplateId: 'interview-scorecard-console',
    tags: ['recruiting', 'scorecard', 'bundle'],
  },
  {
    id: 'recruiting-pilot',
    label: 'Recruiting pilot (#1 + #3)',
    description: 'Single console with screening + scorecard phases, both flows, intake forms, and in-app handoff.',
    multiFlow: 'recruiting-pilot',
    tags: ['recruiting', 'pilot', 'bundle'],
    metadata: {
      bundleId: 'recruiting-pilot',
      handoffNote: 'Click a pass row in screening to switch to scorecard with prefill.',
    },
  },
  {
    id: 'application-type-advisor',
    label: 'SF application-type advisor',
    description: 'Creates SF intake form, CLAW skill flow (if-sf-application-advisor agent loop), and Studio App.',
    formTemplateId: 'application-type-intake',
    flowTemplateId: 'application-type-advisor',
    appTemplateId: 'permit-advisor',
    tags: ['permits', 'planning', 'license', 'claw', 'skills', 'san-francisco', 'bundle'],
    metadata: {
      bundleId: 'application-type-advisor',
      jurisdictionId: 'san-francisco',
      clawSkill: 'if-sf-application-advisor',
      clawTools: [
        'if_lookup_sf_parcel',
        'if_check_sf_land_use',
        'if_advise_application_type',
        'if_scope_sf_requirements',
        'if_estimate_sf_fees',
        'builtin.web_search',
        'if_export_panel_json',
      ],
    },
  },
];
